import { Form, FormFieldOptions, FormResponseValue } from "@tellescope/types-models";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@tellescope/ui";
import { formatStripeProductCost } from "@tellescope/utils";
import { useMemo } from "react";
import { StripeProductSelectionMessage } from "./StripeProductSelectionMessage";
import { resolveErrorMessage } from "../../utils";
import { useStripeProducts } from "./useStripeProducts";

export interface SelectedProduct {
  id: string;
  cost: number;
}

export interface StripeProductSelectionProps {
  productIds: string[];
  enduserId: string;
  formResponses: FormResponseValue[];
  form: Form;
  options: FormFieldOptions;
  selectedProducts: SelectedProduct[];
  onSelectionChange: (selection: SelectedProduct[]) => void;
}

export const StripeProductSelection = ({
  productIds,
  enduserId,
  formResponses,
  form,
  options,
  selectedProducts,
  onSelectionChange,
}: StripeProductSelectionProps) => {
  const { t } = useTranslation("forms");
  const selectedProductIds = useMemo(() => selectedProducts.map(({ id }) => id), [selectedProducts]);
  const { products, prices, error, isLoading } = useStripeProducts({
    productIds,
    enduserId,
    formResponses,
    form,
    options,
  });

  const toggleProduct = (productId: string, cost: number) => {
    const isSelected = selectedProductIds.includes(productId);
    if (isSelected) {
      onSelectionChange(selectedProducts.filter(({ id }) => id !== productId));
    } else if (options.radio) {
      onSelectionChange([{ id: productId, cost }]);
    } else {
      onSelectionChange([...selectedProducts, { id: productId, cost }]);
    }
  };

  if (error) {
    return (
      <StripeProductSelectionMessage
        isError
        heading={t("Product Configuration Error")}
        message={resolveErrorMessage(error, t("Failed to load product information from Stripe")).replace(
          "Stripe pricing error: ",
          ""
        )}
      />
    );
  }

  if (isLoading) {
    return <StripeProductSelectionMessage message={t("Loading product information...")} />;
  }

  if (!products.length) {
    return <StripeProductSelectionMessage message={t("No products are available based on your previous answers.")} />;
  }

  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-sm">
        {productIds.length > 1 ? t("Select Products") : t("Select Product")}
      </p>
      {products.map((product) => {
        const productWithPrice = prices?.find(({ _id }) => _id === product.id);

        if (!productWithPrice) return null;

        return (
          <div
            key={product.id}
            role="button"
            onClick={() => toggleProduct(product.id, product.cost.amount)}
            className="flex gap-4 items-center border rounded-md p-2 hover:bg-gray-100"
          >
            <Checkbox checked={selectedProductIds.includes(product.id)} />
            <div className="space-y-0.5">
              <p className="font-semibold text-sm">{product.title}</p>
              {!!product.description && <p className="text-muted-foreground text-xs">{product.description}</p>}
              <p className="text-xs">{formatStripeProductCost(productWithPrice)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
